/**
 * Ayudas sobre las plantillas de WhatsApp.
 *
 * La copia local guarda los componentes tal y como los devuelve Meta, que no
 * son un tipo cerrado: aqui se leen con comprobaciones en tiempo de ejecucion
 * para poder pintarlos y, sobre todo, para construir los parametros del envio
 * sin que el agente tenga que escribir el nombre ni el JSON a mano.
 */
import type { TemplateView } from '@shared/api-types.ts';

export type TemplateStatus = TemplateView['status'];
export type TemplateCategory = TemplateView['category'];
export type TemplateHeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
export type TemplateButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

/** Tono admitido por el componente `Chip`. */
export type TemplateStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export type TemplateHeader = {
  format: TemplateHeaderFormat;
  /** Vacio en las cabeceras de imagen, video o documento. */
  text: string;
  /** Posiciones `{{n}}` del texto, en orden ascendente. */
  variables: number[];
};

export type TemplateBody = {
  text: string;
  variables: number[];
};

export type TemplateButton = {
  type: TemplateButtonType;
  text: string;
  url: string | null;
  phoneNumber: string | null;
};

export type ParsedTemplate = {
  header: TemplateHeader | null;
  body: TemplateBody;
  footer: string | null;
  buttons: TemplateButton[];
};

export const TEMPLATE_STATUS_LABELS: Record<TemplateStatus, string> = {
  APPROVED: 'Aprobada',
  PENDING: 'En revision',
  REJECTED: 'Rechazada',
  PAUSED: 'Pausada',
  DISABLED: 'Desactivada',
  IN_APPEAL: 'En apelacion',
  PENDING_DELETION: 'Pendiente de borrado',
  DELETED: 'Borrada',
  LIMIT_EXCEEDED: 'Limite de plantillas superado',
};

/** Verde aprobada, ambar en revision, rojo rechazada. */
export const TEMPLATE_STATUS_TONES: Record<TemplateStatus, TemplateStatusTone> = {
  APPROVED: 'success',
  PENDING: 'warning',
  IN_APPEAL: 'warning',
  PAUSED: 'warning',
  PENDING_DELETION: 'warning',
  LIMIT_EXCEEDED: 'warning',
  REJECTED: 'danger',
  DISABLED: 'danger',
  DELETED: 'neutral',
};

/** Orden en el que se ofrecen los estados en el filtro. */
export const TEMPLATE_STATUSES: readonly TemplateStatus[] = [
  'APPROVED',
  'PENDING',
  'REJECTED',
  'PAUSED',
  'DISABLED',
  'IN_APPEAL',
  'PENDING_DELETION',
  'DELETED',
  'LIMIT_EXCEEDED',
];

export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidad',
  AUTHENTICATION: 'Autenticacion',
};

export const TEMPLATE_CATEGORIES: readonly TemplateCategory[] = [
  'UTILITY',
  'MARKETING',
  'AUTHENTICATION',
];

export const TEMPLATE_HEADER_FORMAT_LABELS: Record<TemplateHeaderFormat, string> = {
  TEXT: 'Texto',
  IMAGE: 'Imagen',
  VIDEO: 'Video',
  DOCUMENT: 'Documento',
  LOCATION: 'Ubicacion',
};

export const TEMPLATE_BUTTON_TYPE_LABELS: Record<TemplateButtonType, string> = {
  QUICK_REPLY: 'Respuesta rapida',
  URL: 'Abrir una URL',
  PHONE_NUMBER: 'Llamar a un telefono',
};

export const TEMPLATE_MAX_BUTTONS = 3;
export const TEMPLATE_BUTTON_TEXT_MAX = 25;

/** Calidad que reporta Meta (`GREEN`, `YELLOW`, `RED` o `HIGH`, `MEDIUM`, `LOW`). */
export function qualityLabel(rating: string | null): string {
  if (!rating) return 'Sin datos';
  switch (rating.toUpperCase()) {
    case 'GREEN':
    case 'HIGH':
      return 'Alta';
    case 'YELLOW':
    case 'MEDIUM':
      return 'Media';
    case 'RED':
    case 'LOW':
      return 'Baja';
    default:
      return rating;
  }
}

export function qualityTone(rating: string | null): TemplateStatusTone {
  switch ((rating ?? '').toUpperCase()) {
    case 'GREEN':
    case 'HIGH':
      return 'success';
    case 'YELLOW':
    case 'MEDIUM':
      return 'warning';
    case 'RED':
    case 'LOW':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Posiciones de variable de un texto, sin repetir y en orden.
 * WhatsApp usa parametros posicionales: `{{1}}`, `{{2}}`...
 */
export function extractVariables(text: string): number[] {
  const found = new Set<number>();
  const pattern = /\{\{\s*(\d+)\s*\}\}/g;
  let match = pattern.exec(text);
  while (match !== null) {
    const position = Number(match[1]);
    if (Number.isInteger(position) && position > 0) found.add(position);
    match = pattern.exec(text);
  }
  return [...found].sort((left, right) => left - right);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asHeaderFormat(value: unknown): TemplateHeaderFormat | null {
  return value === 'TEXT' ||
    value === 'IMAGE' ||
    value === 'VIDEO' ||
    value === 'DOCUMENT' ||
    value === 'LOCATION'
    ? value
    : null;
}

function asButtonType(value: unknown): TemplateButtonType | null {
  return value === 'QUICK_REPLY' || value === 'URL' || value === 'PHONE_NUMBER' ? value : null;
}

/** Lee cabecera, cuerpo, pie y botones de los componentes que guarda la copia local. */
export function parseTemplate(template: Pick<TemplateView, 'components'>): ParsedTemplate {
  let header: TemplateHeader | null = null;
  let body: TemplateBody | null = null;
  let footer: string | null = null;
  const buttons: TemplateButton[] = [];

  for (const raw of template.components) {
    const component = asRecord(raw);
    if (!component) continue;

    switch (component.type) {
      case 'HEADER': {
        const format = asHeaderFormat(component.format) ?? 'TEXT';
        const text = asText(component.text);
        header = { format, text, variables: extractVariables(text) };
        break;
      }
      case 'BODY': {
        const text = asText(component.text);
        body = { text, variables: extractVariables(text) };
        break;
      }
      case 'FOOTER': {
        footer = asText(component.text) || null;
        break;
      }
      case 'BUTTONS': {
        const list = Array.isArray(component.buttons) ? component.buttons : [];
        for (const rawButton of list) {
          const button = asRecord(rawButton);
          if (!button) continue;
          const type = asButtonType(button.type);
          if (!type) continue;
          buttons.push({
            type,
            text: asText(button.text),
            url: asText(button.url) || null,
            phoneNumber: asText(button.phone_number) || null,
          });
        }
        break;
      }
      default:
        break;
    }
  }

  return { header, body: body ?? { text: '', variables: [] }, footer, buttons };
}

/** Valores que el agente escribe para cada `{{n}}` (clave: posicion). */
export type TemplateVariableValues = Record<number, string>;

/**
 * Componentes que espera Meta al **enviar** un mensaje de plantilla.
 *
 * Se manda un componente por bloque con parametros: la cabecera solo si es de
 * texto y lleva variables, y el cuerpo cuando las lleva. El pie es texto fijo,
 * asi que no admite parametros y no se envia; el panel lo muestra igualmente
 * para que el agente vea el mensaje completo.
 */
export function buildTemplateSendComponents(
  template: ParsedTemplate,
  headerValues: TemplateVariableValues,
  bodyValues: TemplateVariableValues,
): unknown[] {
  const components: unknown[] = [];

  if (template.header?.format === 'TEXT' && template.header.variables.length > 0) {
    components.push({
      type: 'header',
      parameters: template.header.variables.map((position) => ({
        type: 'text',
        text: headerValues[position]?.trim() ?? '',
      })),
    });
  }

  if (template.body.variables.length > 0) {
    components.push({
      type: 'body',
      parameters: template.body.variables.map((position) => ({
        type: 'text',
        text: bodyValues[position]?.trim() ?? '',
      })),
    });
  }

  return components;
}
